import { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Theme } from '@emotion/react';
import { selectOutputVersions } from '@/redux/selectors/orderPlacementSelectors';
import { updateOutputVersion } from '@/redux/slices/orderPlacementSlice';
import { groupFilesbyParentId } from '@/_utils/titleUtils';
import { deepCopy } from '../../../../_utils/utils';
import OPOutputVersion from './OPOutputVersion';
import { useToast } from '../../../_common/Toast';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';

// *******
// Component on Advanced/Component ordering flow on right side of screen
// of added output versions that can be edited
// *******

const OPOutputVersions = ({ isReview = false }: { isReview?: boolean }) => {
  const dispatch = useAppDispatch();
  const selectedJobs = useAppSelector(selectOutputVersions);
  const [groupedJobsByVersionName, setGroupedJobsByVersionName] = useState([]);
  const [expanded, setExpanded] = useState<number[]>([]);
  const [editMode, setEditMode] = useState<number | null>(null);
  const { success } = useToast();
  const undoJob = useRef(null);

  useEffect(() => {
    return () => {
      setEditMode(null);
      if (undoJob.current) {
        dispatch(updateOutputVersion(undoJob.current));
      }
    };
  }, []);

  useEffect(() => {
    const grouped = selectedJobs.map((selectedJob: any) => ({
      ...selectedJob,
      selections: groupFilesbyParentId(selectedJob.selections)
    }));
    setGroupedJobsByVersionName(grouped);
  }, [selectedJobs]);

  const toggleExpanded = (groupId: number) => {
    setExpanded((prevExpanded) => {
      if (prevExpanded.includes(groupId)) {
        return prevExpanded.filter((id) => id !== groupId);
      } else return [...prevExpanded, groupId];
    });
  };

  const onEdit = (groupId: number) => {
    // save editing job to undo if cancel
    undoJob.current = deepCopy(selectedJobs.find((group: any) => group.id === groupId));
    setEditMode(groupId);
    setExpanded((prevExpanded) => [...prevExpanded, groupId]);
  };

  const onEditCancel = () => {
    setEditMode(null);
    // restore editing job
    if (undoJob.current) {
      dispatch(updateOutputVersion(undoJob.current));
      undoJob.current = null;
    }
  };

  const onSaveEdits = () => {
    setEditMode(null);
    success('Job updated successfully.');
    undoJob.current = null;
  };

  return (
    <StyledJobContainerDiv isReview={isReview}>
      {groupedJobsByVersionName.map((group: any, index) => {
        const files = Object.values(group.selections).reduce((acc: any, files: any) => [...acc, ...files], []);
        return (
          <OPOutputVersion
            key={group.id}
            group={group}
            files={files as InventoryFileEntity[]}
            expanded={expanded.includes(group.id)}
            editMode={editMode === group.id}
            index={index}
            isReview={isReview}
            onEdit={() => onEdit(group.id)}
            onSave={onSaveEdits}
            onCancel={onEditCancel}
            toggleExpand={() => toggleExpanded(group.id)}
          />
        );
      })}
    </StyledJobContainerDiv>
  );
};

export default OPOutputVersions;

type StyledJobContainerDivProps = {
  theme?: Theme;
  isReview: boolean;
};

const StyledJobContainerDiv = styled.div(
  ({ theme, isReview }: StyledJobContainerDivProps) => `
&& {
  margin-top: ${theme?.spacing(2)};

  & .MuiAccordion-root {
    background: ${theme?.palette.background.default + '8a'};
    border-radius: ${theme?.shape.borderRadius}px;
    margin-top: ${theme?.spacing(1)};
    &.Mui-expanded {
      margin-top: ${theme?.spacing(1)};
    }
  }

  & .MuiAccordionDetails-root {
    padding: ${theme?.spacing(0, 3)};
  }

  & .MuiAccordionSummary-root {
    flex-direction: row-reverse;
    padding: ${theme?.spacing(2, 0, 2, 2)};
    min-height: auto;
  }

  & .MuiAccordionSummary-content {
    margin: 0;
  }

  & .MuiAccordionSummary-expandIconWrapper {
    &.Mui-expanded {
      transform: rotate(90deg);
    }
  }

  ${
    isReview
      ? `
    margin-top: 0;
  `
      : null
  };
}
`
);
